import React, { useEffect, useState, useMemo } from "react";
import { apiGet, apiPost } from "../../api";
import { DASHBOARD_HOST, DASHBOARD_URL } from "../../lib/dashboardUrl";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import Pagination from "../../components/Pagination";
import SortableTableHeader from "../../components/SortableTableHeader";
import { CopyHash } from "../../features/web3/forex/components/CopyHash";
import { bscAddressUrl } from "../../lib/format";
import { useSelector, useDispatch } from "react-redux";
import {
  setFilters,
  setTempFilters,
  setPage,
  setSort,
  setUseDates,
  clearUsersListFilters,
} from "../../slices/usersListSlice";
import { MoreHorizontal, UserPlus, X } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";

/* ── Shared UI primitives ─────────────────────────────── */

const inputCls =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-1 focus:ring-ginox-green transition-colors";

const selectCls =
  "w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 transition-colors";

const labelCls =
  "block text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5";

function StatusBadge({ status }) {
  const isActive = status === "Active";
  return (
    <span className={cn("inline-flex items-center gap-1.5 font-mono text-xs", isActive ? "text-ginox-green" : "text-muted-foreground/60")}>
      <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", isActive ? "bg-ginox-green" : "bg-muted-foreground/40")} />
      {status}
    </span>
  );
}

function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ── Main component ───────────────────────────────────── */

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [sortedUsers, setSortedUsers] = useState([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { can, loading: permissionsLoading } = usePermissions();

  const { page, filters, tempFilters, sort, useDates } = useSelector(
    (state) => state.usersList
  );

  const permissions = useMemo(
    () => ({
      canEdit: can("POST:/api/admin/user/:username/edit"),
      canResetPassword: can("POST:/api/admin/user/reset-password"),
      canUpdateStatus: can("POST:/api/admin/user/update-status"),
      canViewStatus: can("GET:/api/admin/user/get-status"),
      canViewIncome: can("GET:/api/admin/user/:username/income"),
      canViewStatement: can("GET:/api/admin/user/:username/statement"),
      canViewStats: can("GET:/api/admin/user/:username/stats"),
      canViewWithdrawStatus: can("GET:/api/admin/user/get-withdraw-status"),
      canUpdateWithdrawStatus: can("POST:/api/admin/user/update-withdraw-status"),
      canConvertToSU: can("POST:/api/admin/user/convert/to-bd"),
      canConvertToNU: can("POST:/api/admin/user/convert/to-nu"),
      canResetEpin: can("POST:/api/admin/user/reset-epin"),
      canReset2FA: can("POST:/api/admin/user/reset-2fa"),
      canLoginAsUser: can("POST:/api/admin/user/:username/token"),
    }),
    [can]
  );

  const fetchUsers = async (pageNum = 1, filterVals = filters) => {
    setLoading(true);
    try {
      let query = `/api/admin/user/list?page=${pageNum}&pageSize=${filterVals.pageSize}&order=desc`;
      if (filterVals.status && filterVals.status !== "All")
        query += `&status=${filterVals.status}`;
      if (filterVals.username && filterVals.username.trim())
        query += `&username=${encodeURIComponent(filterVals.username.trim())}`;
      if (filterVals.role && filterVals.role !== "All")
        query += `&role=${encodeURIComponent(filterVals.role)}`;
      if (useDates)
        query += `&startDate=${filterVals.startDate}&endDate=${filterVals.endDate}`;

      const res = await apiGet(query, {});
      const newUsers = res.data.data.data;
      setUsers(newUsers);
      setSortedUsers(newUsers);
      setTotalPages(res.data.data.totalPages);
      setTotalRows(res.data.data.totalRows || 0);
    } catch {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, filters);
    // eslint-disable-next-line
  }, [page, filters, useDates]);

  const handleTempFilterChange = (e) => {
    dispatch(setTempFilters({ ...tempFilters, [e.target.name]: e.target.value }));
  };

  const applyFilters = () => dispatch(setFilters({ ...tempFilters }));

  const handleDateToggle = (e) => dispatch(setUseDates(e.target.checked));

  const clearFilters = () => dispatch(clearUsersListFilters());

  const handleSort = (key) => {
    const newSort = {
      key,
      direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc",
    };
    dispatch(setSort(newSort));
  };

  useEffect(() => {
    if (!users.length) return;
    const sorted = [...users].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key] ?? "";
      let bValue = b[sort.key] ?? "";
      if (sort.key === "createdAt" || sort.key === "updatedAt") {
        const diff = new Date(aValue).getTime() - new Date(bValue).getTime();
        return sort.direction === "asc" ? diff : -diff;
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      const cmp = aValue.localeCompare(bValue);
      return sort.direction === "asc" ? cmp : -cmp;
    });
    setSortedUsers(sorted);
  }, [users, sort]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Management
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
            All <span className="text-gradient-ginox">Users</span>
          </h1>
        </div>
        {can("POST:/api/admin/user/add") && (
          <button
            onClick={() => navigate("/users/add")}
            className="flex items-center gap-2 rounded-md border border-ginox-green/40 bg-ginox-green/10 px-3 py-2 text-sm font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Add User
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>Username / WalletAddress</label>
            <input type="text" name="username" value={tempFilters.username} onChange={handleTempFilterChange} placeholder="Search…" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select name="status" value={tempFilters.status} onChange={handleTempFilterChange} className={selectCls}>
              <option value="All">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select name="role" value={tempFilters.role} onChange={handleTempFilterChange} className={selectCls}>
              <option value="All">All Roles</option>
              <option value="NU">NU</option>
              <option value="BD">BD</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select name="pageSize" value={tempFilters.pageSize} onChange={handleTempFilterChange} className={selectCls}>
              {[10, 15, 20, 40, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Date toggle */}
        <div className="mt-4 flex items-center gap-2">
          <input id="useDates" type="checkbox" checked={useDates} onChange={handleDateToggle} className="h-4 w-4 rounded border-border accent-ginox-green" />
          <label htmlFor="useDates" className="text-sm text-muted-foreground cursor-pointer select-none">Filter by Date</label>
        </div>

        {useDates && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className={labelCls}>Start Date</label>
              <input type="date" name="startDate" value={tempFilters.startDate} onChange={handleTempFilterChange} max={tempFilters.endDate} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <input type="date" name="endDate" value={tempFilters.endDate} onChange={handleTempFilterChange} min={tempFilters.startDate} max={new Date().toISOString().slice(0, 10)} className={inputCls} />
            </div>
          </div>
        )}

        <div className="mt-4 flex gap-2 flex-wrap">
          <button onClick={applyFilters} className="rounded-md bg-ginox-green px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-opacity">
            Apply Filters
          </button>
          <button onClick={clearFilters} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 min-w-[900px]">
          <thead>
            <tr>
              <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort} className="text-left">S.No</SortableTableHeader>
              <SortableTableHeader sortKey="name" currentSort={sort} onSort={handleSort} className="text-left">Name</SortableTableHeader>
              <SortableTableHeader sortKey="email" currentSort={sort} onSort={handleSort} className="text-left">Email</SortableTableHeader>
              <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort} className="text-left">Username</SortableTableHeader>
              <SortableTableHeader sortKey="role" currentSort={sort} onSort={handleSort} className="text-left">Role</SortableTableHeader>
              <SortableTableHeader sortKey="accountStatus" currentSort={sort} onSort={handleSort} className="text-left">Status</SortableTableHeader>
              <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort} className="text-left">Registered At</SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-left">Wallet Address</SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-center">Wallets</SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-center">Reports</SortableTableHeader>
              <SortableTableHeader sortable={false} className="text-center">Actions</SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 11 }).map((_, j) => (
                    <td key={j} className="border-b border-border/40 px-5 py-3.5">
                      <div className="h-4 w-full max-w-[120px] rounded bg-muted/30 animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={11} className="border-b border-border/40 px-4 py-12 text-center text-sm text-muted-foreground">
                  No users found
                </td>
              </tr>
            ) : (
              sortedUsers.map((u, index) => (
                <tr key={u._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 font-mono text-xs text-muted-foreground">
                    {(page - 1) * filters.pageSize + index + 1}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm font-medium text-foreground whitespace-nowrap">
                    {u.name} {u.surname}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <span className="font-mono text-xs text-muted-foreground">{u.email}</span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <span className="font-mono text-xs text-foreground/80">{u.username}</span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <span className="font-mono text-xs text-muted-foreground">{u.role}</span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <StatusBadge status={u.accountStatus} />
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5">
                    <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(u.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-xs text-muted-foreground">
                    <CopyHash value={u.walletAddress} href={bscAddressUrl(u.walletAddress)} head={6} tail={4} />
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-center">
                    <WalletActionsDropdown username={u.username} />
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-center">
                    <ReportsDropdown username={u.username} />
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-center">
                    <UserActionsDropdown user={u} permissions={permissions} permissionsLoading={permissionsLoading} onRefresh={() => fetchUsers(page, filters)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalRows={totalRows}
        pageSize={filters.pageSize}
        onPageChange={(newPage) => dispatch(setPage(newPage))}
      />
    </div>
  );
};

export default UsersList;

/* ── Dropdown button shared style ────────────────────── */

const DotsButton = React.forwardRef(({ label, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.06] hover:text-foreground transition-colors"
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
  </button>
));

/* ── User actions dropdown ────────────────────────────── */

function UserActionsDropdown({ user, permissions, permissionsLoading, onRefresh }) {
  const navigate = useNavigate();
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawStatus, setWithdrawStatus] = useState(null);
  const [loadingWithdraw, setLoadingWithdraw] = useState(false);
  const [withdrawComment, setWithdrawComment] = useState("");
  const [showConvertSU, setShowConvertSU] = useState(false);
  const [convertSUForm, setConvertSUForm] = useState({ sponsor_id: "", updateSponsor: "1" });
  const [loadingConvert, setLoadingConvert] = useState(false);
  const [showLoginConfirm, setShowLoginConfirm] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [showResetEpin, setShowResetEpin] = useState(false);
  const [loadingResetEpin, setLoadingResetEpin] = useState(false);
  const [newEpin, setNewEpin] = useState("");
  const [showReset2FA, setShowReset2FA] = useState(false);
  const [loadingReset2FA, setLoadingReset2FA] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);
  const fetchWithdrawStatus = async () => {
    const res = await apiGet(`/api/admin/user/get-withdraw-status?username=${user.username}`);
    if (res.data.success) setWithdrawStatus(res.data.data);
    else toast.error(res.data.message || "Failed to fetch withdraw status");
  };

  const handleViewWithdraw = async () => {
    setLoadingWithdraw(true);
    setShowWithdraw(true);
    try { await fetchWithdrawStatus(); }
    catch { toast.error("Error fetching withdraw status"); }
    finally { setLoadingWithdraw(false); }
  };

  // Toggle, not a setter: the server flips whatever is stored. Re-read after so changedBy/changedAt are fresh.
  const handleToggleWithdraw = async () => {
    setLoadingWithdraw(true);
    try {
      const res = await apiPost("/api/admin/user/update-withdraw-status", { username: user.username, comment: withdrawComment.trim() });
      if (res.data.success) {
        toast.success("Withdraw status updated");
        setWithdrawComment("");
        await fetchWithdrawStatus();
      }
      else toast.error(res.data.message || "Failed to update withdraw status");
    } catch (err) { toast.error(err.response?.data?.message || "Error updating withdraw status"); }
    finally { setLoadingWithdraw(false); }
  };

  const handleConvertSU = async (e) => {
    e.preventDefault();
    setLoadingConvert(true);
    try {
      const res = await apiPost("/api/admin/user/convert/to-bd", { username: user.username, ...convertSUForm });
      if (res.data.success) { toast.success("User converted to BD"); setShowConvertSU(false); }
      else toast.error(res.data.message || "Failed to convert to BD");
    } catch { toast.error("Error converting to BD"); }
    finally { setLoadingConvert(false); }
  };

  const handleConvertNU = async () => {
    setLoadingConvert(true);
    try {
      const res = await apiPost("/api/admin/user/convert/to-nu", { username: user.username });
      if (res.data.success) toast.success("User converted to NU");
      else toast.error(res.data.message || "Failed to convert to NU");
    } catch { toast.error("Error converting to NU"); }
    finally { setLoadingConvert(false); }
  };

  const handleLoginAsUser = async () => {
    setLoadingLogin(true);
    let newWindow = null;
    try {
      newWindow = window.open("about:blank", "_blank");
      if (!newWindow) throw new Error("Pop-up blocked. Please allow pop-ups for this site.");
      const res = await apiPost(`/api/admin/user/${user.username}/token`, {});
      if (res.data.success) {
        const { name, surname, email, role } = res.data.data;
        const token = res.data.token;
        newWindow.location.href = `${DASHBOARD_URL}/admin-signin?username=${encodeURIComponent(user.username)}&name=${encodeURIComponent(name + " " + surname)}&email=${encodeURIComponent(email)}&role=${encodeURIComponent(role)}&token=${encodeURIComponent(token)}`;
        toast.success("Redirecting to user dashboard...");
        setShowLoginConfirm(false);
      } else {
        newWindow?.close();
        toast.error(res.data.message || "Failed to generate user token");
      }
    } catch (err) {
      newWindow?.close();
      toast.error(err.message || "Error generating user token");
    } finally { setLoadingLogin(false); }
  };

  const handleResetEpin = async () => {
    setLoadingResetEpin(true);
    try {
      const res = await apiPost("/api/admin/user/reset-epin", { username: user.username });
      if (res.data.success) { setNewEpin(res.data.ePin); toast.success("E-PIN reset successfully"); }
      else toast.error(res.data.message || "Failed to reset E-PIN");
    } catch { toast.error("Error resetting E-PIN"); }
    finally { setLoadingResetEpin(false); }
  };

  const handleReset2FA = async () => {
    setLoadingReset2FA(true);
    try {
      const res = await apiPost("/api/admin/user/reset-2fa", { username: user.username });
      if (res.data.success) { toast.success("2FA reset successfully"); setShowReset2FA(false); }
      else toast.error(res.data.message || "Failed to reset 2FA");
    } catch { toast.error("Error resetting 2FA"); }
    finally { setLoadingReset2FA(false); }
  };

  const handleUpdateStatus = async () => {
    setLoadingUpdateStatus(true);
    try {
      const res = await apiPost("/api/admin/user/update-status", { username: user.username });
      if (res.data.success) { toast.success(`Status updated${res.data.status ? ` to ${res.data.status}` : ""}`); setShowUpdateStatus(false); onRefresh?.(); }
      else toast.error(res.data.message || "Failed to update status");
    } catch (err) { toast.error(err.response?.data?.message || "Error updating status"); }
    finally { setLoadingUpdateStatus(false); }
  };

  const showAccount =
    permissions.canUpdateStatus ||
    permissions.canViewStatus ||
    (permissions.canConvertToSU && user.role === "NU") ||
    (permissions.canConvertToNU && user.role === "BD");
  const showSecurity =
    permissions.canResetPassword ||
    permissions.canResetEpin ||
    permissions.canReset2FA ||
    permissions.canLoginAsUser;

  return (
    <div className="inline-block text-left">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DotsButton label="User actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56">
          {permissionsLoading ? (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-muted-foreground">
              <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />
              Loading…
            </div>
          ) : (
            <>
              <DropdownMenuLabel>Profile</DropdownMenuLabel>
              <DropdownMenuItem variant="primary" onSelect={() => navigate(`/users/${user.username}/profile`, { state: { user } })}>View Profile</DropdownMenuItem>
              {permissions.canEdit && <DropdownMenuItem onSelect={() => navigate(`/users/edit/${user.username}?name=${encodeURIComponent(user.name || "")}&surname=${encodeURIComponent(user.surname || "")}&email=${encodeURIComponent(user.email || "")}`)}>Edit User</DropdownMenuItem>}

              {showAccount && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Account</DropdownMenuLabel>
                  {permissions.canUpdateStatus && <DropdownMenuItem onSelect={() => setShowUpdateStatus(true)}>Update Status</DropdownMenuItem>}
                  {permissions.canViewStatus && <DropdownMenuItem onSelect={() => navigate("/users/view-status", { state: { username: user.username } })}>View Status</DropdownMenuItem>}
                  {permissions.canConvertToSU && user.role === "NU" && <DropdownMenuItem onSelect={() => setShowConvertSU(true)}>Convert to BD</DropdownMenuItem>}
                  {permissions.canConvertToNU && user.role === "BD" && <DropdownMenuItem onSelect={() => handleConvertNU()}>Convert to NU</DropdownMenuItem>}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuLabel>Wallet &amp; Credit</DropdownMenuLabel>
              {permissions.canViewIncome && <DropdownMenuItem onSelect={() => navigate(`/users/${user.username}/income`)}>Income</DropdownMenuItem>}
              {permissions.canViewStatement && <DropdownMenuItem onSelect={() => navigate(`/users/${user.username}/statement`)}>Statement</DropdownMenuItem>}
              {permissions.canViewStats && <DropdownMenuItem onSelect={() => navigate(`/users/${user.username}/stats`)}>Stats</DropdownMenuItem>}
              <DropdownMenuItem onSelect={() => navigate(`/credit-stats/user/${user.username}`)}>Credit Stats</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => navigate(`/reports/wallet-statement?username=${user.username}&walletType=APP_CREDIT`)}>Credit Statement</DropdownMenuItem>
              {permissions.canViewWithdrawStatus && <DropdownMenuItem onSelect={() => handleViewWithdraw()}>View Withdraw Status</DropdownMenuItem>}
              {showSecurity && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Security</DropdownMenuLabel>
                  {permissions.canResetPassword && <DropdownMenuItem onSelect={() => navigate(`/users/update-password/${user.username}`)}>Reset Password</DropdownMenuItem>}
                  {permissions.canResetEpin && <DropdownMenuItem onSelect={() => setShowResetEpin(true)}>Reset E-PIN</DropdownMenuItem>}
                  {permissions.canReset2FA && <DropdownMenuItem onSelect={() => setShowReset2FA(true)}>Reset 2FA</DropdownMenuItem>}
                  {permissions.canLoginAsUser && <DropdownMenuItem variant="primary" onSelect={() => setShowLoginConfirm(true)}>Login as User</DropdownMenuItem>}
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Withdraw Status Modal */}
      <Modal open={showWithdraw} onClose={() => setShowWithdraw(false)} title="Withdraw Status">
        {loadingWithdraw ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><div className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />Loading…</div>
        ) : (
          <>
            <div className="space-y-1 mb-4 text-sm text-muted-foreground">
              <p>Withdraw Disabled: <span className="font-mono font-medium text-foreground">{withdrawStatus?.withdraw_disabled ? "Yes" : "No"}</span></p>
              {withdrawStatus?.comment && <p>Reason: <span className="text-foreground whitespace-pre-wrap break-words">{withdrawStatus.comment}</span></p>}
              {withdrawStatus?.changedBy && <p>Changed By: <span className="text-foreground">{withdrawStatus.changedBy}</span></p>}
              {withdrawStatus?.changedAt && <p>Changed At: <span className="font-mono text-foreground">{new Date(withdrawStatus.changedAt).toLocaleString()}</span></p>}
            </div>
            {permissions.canUpdateWithdrawStatus && withdrawStatus && (
              <div className="space-y-3">
                <div>
                  <textarea value={withdrawComment} onChange={(e) => setWithdrawComment(e.target.value)} maxLength={500} rows={3}
                    placeholder={withdrawStatus.withdraw_disabled ? "Reason for re-enabling (optional)" : "Why are withdrawals being disabled?"}
                    className={inputCls} />
                  <p className="text-right text-xs text-muted-foreground">{withdrawComment.length}/500</p>
                </div>
                <button onClick={handleToggleWithdraw} disabled={loadingWithdraw} className="rounded-md bg-ginox-green px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
                  {loadingWithdraw ? "Updating…" : withdrawStatus.withdraw_disabled ? "Enable Withdrawals" : "Disable Withdrawals"}
                </button>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Convert to BD Modal */}
      <Modal open={showConvertSU} onClose={() => setShowConvertSU(false)} title="Convert to BD">
        <form onSubmit={handleConvertSU} className="space-y-4">
          <input name="sponsor_id" value={convertSUForm.sponsor_id} onChange={(e) => setConvertSUForm((f) => ({ ...f, sponsor_id: e.target.value }))} placeholder="Sponsor ID" className={inputCls} required />
          <div>
            <label className={labelCls}>Update Sponsor?</label>
            <select name="updateSponsor" value={convertSUForm.updateSponsor} onChange={(e) => setConvertSUForm((f) => ({ ...f, updateSponsor: e.target.value }))} className={selectCls}>
              <option value="1">Yes</option>
              <option value="0">No</option>
            </select>
          </div>
          <button type="submit" disabled={loadingConvert} className="rounded-md bg-ginox-green px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
            {loadingConvert ? "Converting…" : "Convert"}
          </button>
        </form>
      </Modal>

      {/* Login as User Modal */}
      <Modal open={showLoginConfirm} onClose={() => setShowLoginConfirm(false)} title="Login as User">
        <p className="text-sm text-muted-foreground mb-4">You will be redirected to <strong className="text-foreground">{DASHBOARD_HOST}/admin-signin</strong> in a new tab with this user's credentials.</p>
        <div className="flex justify-end gap-2">
          <button onClick={() => setShowLoginConfirm(false)} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">Cancel</button>
          <button onClick={handleLoginAsUser} disabled={loadingLogin} className="rounded-md bg-ginox-green px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
            {loadingLogin ? "Processing…" : "Confirm & Redirect"}
          </button>
        </div>
      </Modal>

      {/* Reset E-PIN Modal */}
      <Modal open={showResetEpin} onClose={() => { setShowResetEpin(false); setNewEpin(""); }} title="Reset E-PIN">
        {newEpin ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">E-PIN reset for <strong className="text-foreground">{user.username}</strong>.</p>
            <div className="rounded-md border border-ginox-green/30 bg-ginox-green/5 p-3">
              <p className="text-xs text-muted-foreground mb-1">New E-PIN</p>
              <p className="font-mono text-lg font-semibold text-ginox-green">{newEpin}</p>
            </div>
            <div className="flex justify-end">
              <button onClick={() => { setShowResetEpin(false); setNewEpin(""); }} className="rounded-md bg-ginox-green px-4 py-2 text-sm font-medium text-white hover:opacity-90">Close</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Reset the E-PIN for <strong className="text-foreground">{user.username}</strong>?</p>
            <p className="text-xs text-destructive">This will generate a new E-PIN and invalidate the current one.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowResetEpin(false)} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">Cancel</button>
              <button onClick={handleResetEpin} disabled={loadingResetEpin} className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
                {loadingResetEpin ? "Resetting…" : "Reset E-PIN"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Update Status Modal */}
      <Modal open={showUpdateStatus} onClose={() => setShowUpdateStatus(false)} title="Update Account Status">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Toggle the account status for <strong className="text-foreground">{user.username}</strong>?</p>
          <p className="text-xs text-muted-foreground">An active account will be deactivated, and an inactive one reactivated.</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowUpdateStatus(false)} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">Cancel</button>
            <button onClick={handleUpdateStatus} disabled={loadingUpdateStatus} className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-sm font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50">
              {loadingUpdateStatus ? "Updating…" : "Update Status"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reset 2FA Modal */}
      <Modal open={showReset2FA} onClose={() => setShowReset2FA(false)} title="Reset 2FA">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Reset 2FA for <strong className="text-foreground">{user.username}</strong>?</p>
          <p className="text-xs text-destructive">This will disable 2FA authentication. The user will need to set it up again.</p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowReset2FA(false)} className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-sm text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">Cancel</button>
            <button onClick={handleReset2FA} disabled={loadingReset2FA} className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">
              {loadingReset2FA ? "Resetting…" : "Reset 2FA"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ── Wallet actions dropdown ──────────────────────────── */

function WalletActionsDropdown({ username }) {
  const [showBalance, setShowBalance] = useState(false);
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showDeduct, setShowDeduct] = useState(false);
  const [amount, setAmount] = useState("");
  // Admins only: the user's statement line is written by the server.
  const [note, setNote] = useState("");
  const [loadingAction, setLoadingAction] = useState(false);
  const navigate = useNavigate();

  const handleViewBalance = async () => {
    setLoadingBalance(true);
    setShowBalance(true);
    try {
      const res = await apiGet(`/api/admin/wallets/BFM/available-balance?username=${username}`);
      if (res.data.success) setBalance(res.data.data.balance);
      else toast.error(res.data.message || "Failed to fetch balance");
    } catch { toast.error("Error fetching balance"); }
    finally { setLoadingBalance(false); }
  };

  const handleAddBalance = async (e) => {
    e.preventDefault();
    setLoadingAction(true);
    try {
      const res = await apiPost("/api/admin/wallets/BFM/add-balance", { username, amount, note: note.trim() });
      if (res.data.success) { toast.success(`Added ${amount} BFM to ${username}`); setShowAdd(false); setAmount(""); setNote(""); }
      else toast.error(res.data.message || "Failed to add balance");
    } catch { toast.error("Error adding balance"); }
    finally { setLoadingAction(false); }
  };

  const handleDeductBalance = async (e) => {
    e.preventDefault();
    setLoadingAction(true);
    try {
      const res = await apiPost("/api/admin/wallets/BFM/deduct-balance", { username, amount, note: note.trim() });
      if (res.data.success) { toast.success(`Deducted ${amount} BFM from ${username}`); setShowDeduct(false); setAmount(""); setNote(""); }
      else toast.error(res.data.message || "Failed to deduct balance");
    } catch { toast.error("Error deducting balance"); }
    finally { setLoadingAction(false); }
  };

  return (
    <div className="inline-block text-left">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DotsButton label="Wallet actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-52">
          <DropdownMenuItem onSelect={() => handleViewBalance()}>View BFM Balance</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setShowAdd(true)}>Add BFM Balance</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setShowDeduct(true)}>Deduct BFM Balance</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate(`/wallets/gusd?username=${encodeURIComponent(username)}`)}>View GUSD Wallet</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate(`/wallets/forex?username=${encodeURIComponent(username)}`)}>View FOREX Wallet</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Modal open={showBalance} onClose={() => setShowBalance(false)} title="BFM Wallet Balance">
        {loadingBalance ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><div className="h-4 w-4 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />Loading…</div>
        ) : (
          <p className="text-sm text-muted-foreground">Balance: <span className="font-mono font-medium text-foreground">{balance}</span></p>
        )}
      </Modal>

      <Modal open={showAdd} onClose={() => { setShowAdd(false); setAmount(""); setNote(""); }} title="Add BFM Balance">
        <form onSubmit={handleAddBalance} className="space-y-4">
          <input name="amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className={inputCls} required type="number" min="0.01" step="0.01" />
          <textarea name="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional, admins only)" rows={3} className={inputCls} />
          <button type="submit" disabled={loadingAction} className="rounded-md bg-ginox-green px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">{loadingAction ? "Adding…" : "Add"}</button>
        </form>
      </Modal>

      <Modal open={showDeduct} onClose={() => { setShowDeduct(false); setAmount(""); setNote(""); }} title="Deduct BFM Balance">
        <form onSubmit={handleDeductBalance} className="space-y-4">
          <input name="amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" className={inputCls} required type="number" min="0.01" step="0.01" />
          <textarea name="note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional, admins only)" rows={3} className={inputCls} />
          <button type="submit" disabled={loadingAction} className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50">{loadingAction ? "Deducting…" : "Deduct"}</button>
        </form>
      </Modal>
    </div>
  );
}

/* ── Reports dropdown ─────────────────────────────────── */

function ReportsDropdown({ username }) {
  const navigate = useNavigate();

  return (
    <div className="inline-block text-left">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <DotsButton label="Reports actions" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-52">
          <DropdownMenuItem onSelect={() => navigate(`/users/${username}/sponsor-bonus-report`)}>Income History</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate(`/users/${username}/wallet-statement-report`)}>Wallet History</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
